/* Emergency kill switch (specification section 23).
 *
 * Always visible in the status bar, never scrolled away. Engaging it is
 * deliberately friction-ful: a scope, a reason and an explicit confirmation
 * are required, because this is the control that stops a runaway.
 *
 * `flatten_positions` is OFF by default. Flattening realises P&L and is not
 * always the safe action — it must be chosen consciously, not inherited.
 */
import { useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useStore } from '@/store'

export function KillSwitch() {
  const engaged = useStore((s) => s.killSwitchEngaged)
  const setEngaged = useStore((s) => s.setKillSwitch)
  const can = useStore((s) => s.can)

  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [flatten, setFlatten] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const permitted = can('risk:killswitch')

  async function engage() {
    setBusy(true); setError(null)
    try {
      await api.killSwitch({
        scope: 'ORGANIZATION',
        scope_id: '00000000-0000-0000-0000-000000000000',
        reason,
        block_new_orders: true,
        cancel_open_orders: true,
        stop_strategies: true,
        flatten_positions: flatten,
      })
      setEngaged(true)
      setOpen(false)
      setReason('')
      setFlatten(false)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function release() {
    setBusy(true); setError(null)
    try {
      await api.releaseKillSwitch(reason || 'released by operator after review')
      setEngaged(false)
      setOpen(false)
      setReason('')
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        onClick={() => permitted && setOpen(true)}
        disabled={!permitted}
        title={permitted
          ? (engaged ? 'Kill switch is ENGAGED — click to review' : 'Emergency stop')
          : 'Your role does not permit the kill switch'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)',
          padding: '2px var(--sp-3)',
          background: engaged ? 'var(--danger)' : 'var(--danger-bg)',
          border: '1px solid var(--danger)',
          borderRadius: 'var(--radius-sm)',
          color: engaged ? '#fff' : 'var(--danger)',
          fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.06em',
          cursor: permitted ? 'pointer' : 'not-allowed',
          opacity: permitted ? 1 : 0.45,
        }}
      >
        <span aria-hidden>⏻</span>
        {engaged ? 'ENGAGED' : 'KILL'}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Emergency kill switch"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.65)',
            display: 'grid', placeItems: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
        >
          <div style={{
            width: 480, maxWidth: '92vw',
            background: 'var(--bg-panel)',
            border: '2px solid var(--danger)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            padding: 'var(--sp-5)',
          }}>
            <h2 style={{
              fontSize: 'var(--fs-lg)', color: 'var(--danger)',
              marginBottom: 'var(--sp-3)', letterSpacing: '0.02em',
            }}>
              {engaged ? 'Release kill switch' : 'Emergency kill switch'}
            </h2>

            {!engaged ? (
              <>
                <p style={{
                  fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                  marginBottom: 'var(--sp-4)', lineHeight: 1.55,
                }}>
                  This will immediately <strong>block new orders</strong>,{' '}
                  <strong>cancel all working orders</strong> and{' '}
                  <strong>stop all strategies</strong> for your organization.
                </p>

                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)',
                  padding: 'var(--sp-3)', marginBottom: 'var(--sp-4)',
                  background: 'var(--warn-bg)', border: '1px solid var(--warn)',
                  borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={flatten}
                         onChange={(e) => setFlatten(e.target.checked)}
                         style={{ marginTop: 2 }} />
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--warn)' }}>
                    Also <strong>flatten open positions</strong>. This realises
                    profit and loss immediately and cannot be undone. Leave it
                    unchecked unless you specifically intend to close out.
                  </span>
                </label>
              </>
            ) : (
              <p style={{
                fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                marginBottom: 'var(--sp-4)', lineHeight: 1.55,
              }}>
                Releasing re-enables trading. Do this only once the cause has
                been identified and positions have been reconciled with the venue.
              </p>
            )}

            <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
              <span style={{
                display: 'block', fontSize: 'var(--fs-2xs)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--text-muted)', marginBottom: 'var(--sp-1)',
              }}>
                Reason (recorded in the audit trail, minimum 10 characters)
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. runaway strategy on NIFTY futures; halting to investigate"
                style={{
                  width: '100%', padding: 'var(--sp-2)',
                  background: 'var(--bg-input)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
                  fontSize: 'var(--fs-sm)', fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </label>

            {error && (
              <div style={{
                padding: 'var(--sp-2)', marginBottom: 'var(--sp-3)',
                background: 'var(--danger-bg)', border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontSize: 'var(--fs-xs)',
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <button onClick={() => setOpen(false)} style={{
                padding: 'var(--sp-2) var(--sp-4)',
                background: 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 'var(--fs-sm)',
              }}>
                Cancel
              </button>
              <button
                onClick={engaged ? release : engage}
                disabled={busy || reason.trim().length < 10}
                style={{
                  padding: 'var(--sp-2) var(--sp-4)',
                  background: reason.trim().length < 10 ? 'var(--bg-hover)' : 'var(--danger)',
                  border: 'none', borderRadius: 'var(--radius-sm)',
                  color: reason.trim().length < 10 ? 'var(--text-muted)' : '#fff',
                  fontWeight: 700, fontSize: 'var(--fs-sm)',
                  cursor: reason.trim().length < 10 ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? 'Working…' : engaged ? 'Release and resume trading' : 'Engage kill switch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
